import React from 'react';
import { AlertTriangle, User, Zap } from 'lucide-react';
import { clampOccupationPercent, sanitizeMetricNumber } from '../../utils/dashboardKpi';

interface YamazumiData {
  name: string;
  trabalho: number;
  espera: number;
  disponibilidade: number;
  ocupacao: number;
}

interface YamazumiChartProps {
  data: YamazumiData[];
  onSelect?: (name: string | null) => void;
  selectedItem?: string | null;
  maxHeight?: number;
  taktTime?: number;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  item: YamazumiData | null;
  total: number;
  efficiency: number;
  excessPercent: number;
}

const COLORS = {
  tra: '#2563EB',
  nvaPattern: 'repeating-linear-gradient(135deg, #f8fafc 0px, #f8fafc 5px, #eef2f7 5px, #eef2f7 10px)',
  takt: '#EF4444',
};

/** Margem superior do eixo Y para rótulos e linha do takt */
const Y_HEADROOM_RATIO = 1.18;
const LABEL_ZONE_PX = 36;

export const YamazumiChart: React.FC<YamazumiChartProps> = ({
  data,
  onSelect,
  selectedItem,
  maxHeight = 300,
  taktTime = 12,
}) => {
  const safeData = (Array.isArray(data) ? data : []).map((item) => ({
    ...item,
    trabalho: sanitizeMetricNumber(item.trabalho, 0),
    espera: sanitizeMetricNumber(item.espera, 0),
    disponibilidade: sanitizeMetricNumber(item.disponibilidade, 0),
    ocupacao: clampOccupationPercent(Number(item.ocupacao) || 0),
  }));
  const chartRef = React.useRef<HTMLDivElement | null>(null);

  const [tooltip, setTooltip] = React.useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    item: null,
    total: 0,
    efficiency: 0,
    excessPercent: 0,
  });

  const safeTakt = sanitizeMetricNumber(taktTime, 0) > 0.05 ? sanitizeMetricNumber(taktTime, 0) : 480;
  const maxResourceLoad = Math.max(...safeData.map((d) => d.trabalho + d.espera), 0);
  const limiteSuperior = Math.max(safeTakt, maxResourceLoad, 1) * Y_HEADROOM_RATIO;

  const plotTopOffset = LABEL_ZONE_PX;
  const plotBottomOffset = 10;
  const plotHeight = Math.max(170, maxHeight - (plotTopOffset + plotBottomOffset));

  const avgOccupancy = safeData.length > 0
    ? clampOccupationPercent(safeData.reduce((sum, d) => sum + d.ocupacao, 0) / safeData.length)
    : 0;
  const overloadedCount = safeData.filter((d) => d.trabalho + d.espera > safeTakt).length;

  const setTooltipFromEvent = (e: React.MouseEvent, item: YamazumiData) => {
    if (!chartRef.current) return;

    const rect = chartRef.current.getBoundingClientRect();
    const total = Math.max(0, item.trabalho + item.espera);
    const efficiency = total > 0 ? (item.trabalho / total) * 100 : 0;
    const excessPercent = safeTakt > 0 && total > safeTakt
      ? ((total / safeTakt) - 1) * 100
      : 0;

    setTooltip({
      visible: true,
      x: Math.min(Math.max(e.clientX - rect.left + 10, 8), rect.width - 280),
      y: Math.min(Math.max(e.clientY - rect.top - 72, 8), rect.height - 140),
      item,
      total,
      efficiency,
      excessPercent,
    });
  };

  const hideTooltip = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const formatMinutesShort = (value: number) => {
    const safeValue = sanitizeMetricNumber(value, 0);
    if (safeValue <= 0) return '0m';
    if (safeValue >= 10_000) return `${Math.round(safeValue / 60)}h`;
    if (safeValue > 0 && safeValue < 1) {
      const seconds = safeValue * 60;
      return `${seconds < 10 ? seconds.toFixed(1) : seconds.toFixed(0)}s`;
    }
    return `${safeValue < 10 ? safeValue.toFixed(1) : Math.round(safeValue)}m`;
  };

  if (safeData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500 text-sm">
        <User className="mr-2" size={16} />
        Adicione responsaveis aos cards para analise
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans">
      <div className="flex items-center justify-between mb-3 px-2 py-1.5 bg-slate-100/80 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
          <span className="flex items-center gap-1.5" title="Tempo de processamento (valor agregado)">
            <div
              className="w-4 h-3 rounded-none border border-slate-800/80"
              style={{ background: COLORS.tra }}
            />
            Trabalho (TRA)
          </span>
          <span className="flex items-center gap-1.5" title="Tempo de espera / não agrega valor">
            <div
              className="w-4 h-3 rounded-none border border-dashed border-slate-400"
              style={{ background: COLORS.nvaPattern }}
            />
            Espera (NVA)
          </span>
          <span className="flex items-center gap-1.5" title="Ritmo exigido pelo cliente">
            <div className="w-5 border-b-2 border-dashed border-red-500/70" />
            Takt Time
          </span>
        </div>

        <div className="flex items-center gap-2 text-[11px]">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <Zap size={11} className={avgOccupancy > 100 ? 'text-amber-500' : 'text-emerald-500'} />
            <span className="text-slate-600 dark:text-slate-400">Media:</span>
            <b className={avgOccupancy > 100 ? 'text-amber-600' : 'text-emerald-600'}>{avgOccupancy.toFixed(0)}%</b>
          </span>
        </div>
      </div>

      <div
        ref={chartRef}
        className="flex-1 relative px-2 py-2 bg-white dark:bg-[var(--bg-surface)] border border-slate-200 dark:border-slate-700 rounded-sm overflow-visible"
        style={{ height: maxHeight, minHeight: maxHeight }}
      >
        <div
          className="absolute left-0 w-11 flex flex-col justify-between text-[9px] text-slate-400 dark:text-slate-500 font-medium text-right pr-1"
          style={{ top: `${plotTopOffset}px`, bottom: `${plotBottomOffset}px` }}
        >
          <span>{Math.round(limiteSuperior)}m</span>
          <span>{Math.round(limiteSuperior * 0.75)}m</span>
          <span>{Math.round(limiteSuperior * 0.5)}m</span>
          <span>{Math.round(limiteSuperior * 0.25)}m</span>
          <span>0</span>
        </div>

        <div
          className="absolute left-11 right-2 pointer-events-none flex flex-col justify-between divide-y divide-slate-100 dark:divide-neutral-800"
          style={{ top: `${plotTopOffset}px`, bottom: `${plotBottomOffset}px` }}
        >
          <div /><div /><div /><div /><div />
        </div>

        <div
          className="absolute left-11 right-2 z-30 pointer-events-none border-b-2 border-dashed border-red-500/60"
          style={{ bottom: `${plotBottomOffset + (safeTakt / limiteSuperior) * plotHeight}px` }}
        >
          <span className="absolute z-40 -top-4 right-0 text-[10px] font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 rounded-sm whitespace-nowrap">
            Takt — {formatMinutesShort(safeTakt)}
          </span>
        </div>

        <div
          className="absolute z-10 left-11 right-2 flex items-end gap-1 overflow-visible"
          style={{ bottom: `${plotBottomOffset}px`, height: `${plotHeight + plotTopOffset}px`, paddingTop: `${plotTopOffset}px` }}
        >
          {safeData.map((item, idx) => {
            const isSelected = selectedItem === item.name;
            const total = Math.max(0, item.trabalho + item.espera);
            const rawHeight = (total / limiteSuperior) * plotHeight;
            const totalHeight = total > 0 ? Math.max(3, rawHeight) : 0;
            const exceedsTakt = total > safeTakt;

            const traRatio = total > 0 ? item.trabalho / total : 0;
            const nvaRatio = total > 0 ? item.espera / total : 0;
            const traHeight = totalHeight * traRatio;
            const nvaHeight = totalHeight * nvaRatio;

            return (
              <div
                key={`${item.name}-${idx}`}
                className="flex-1 min-w-[98px] max-w-none flex flex-col items-center cursor-pointer group overflow-visible"
                style={{ height: `${plotHeight + plotTopOffset}px`, justifyContent: 'flex-end' }}
                onClick={() => onSelect?.(isSelected ? null : item.name)}
                onMouseEnter={(e) => setTooltipFromEvent(e, item)}
                onMouseMove={(e) => setTooltipFromEvent(e, item)}
                onMouseLeave={hideTooltip}
              >
                <div
                  className={`shrink-0 text-[10px] font-bold mb-1 px-1 py-0.5 rounded-sm border tabular-nums ${
                    exceedsTakt
                      ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {item.ocupacao.toFixed(0)}%
                </div>

                <div
                  className={`w-full relative box-border rounded-none overflow-hidden transition-all border ${
                    isSelected
                      ? 'border-blue-600 ring-1 ring-blue-500/30'
                      : exceedsTakt
                        ? 'border-red-500 hover:border-red-600'
                        : 'border-slate-300 hover:border-slate-500'
                  }`}
                  style={{ height: `${totalHeight}px` }}
                >
                  {item.espera > 0 && (
                    <div
                      className="absolute top-0 left-0 right-0 border-b border-dashed border-slate-400 flex items-center justify-center"
                      style={{
                        height: `${nvaHeight}px`,
                        background: COLORS.nvaPattern,
                      }}
                    >
                      {nvaHeight >= 14 && (
                        <span className="text-[9px] font-semibold text-slate-600 tabular-nums leading-none">
                          {formatMinutesShort(item.espera)}
                        </span>
                      )}
                    </div>
                  )}

                  {item.trabalho > 0 && (
                    <div
                      className="absolute bottom-0 left-0 right-0 shadow-sm flex items-center justify-center"
                      style={{
                        height: `${traHeight}px`,
                        background: COLORS.tra,
                      }}
                    >
                      {traHeight >= 14 && (
                        <span className="text-[9px] font-bold text-white tabular-nums leading-none">
                          {formatMinutesShort(item.trabalho)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-1.5 text-[10px] font-semibold text-slate-600 dark:text-slate-400 text-center truncate w-full px-1 shrink-0" title={item.name}>
                  {item.name}
                </div>
              </div>
            );
          })}
        </div>

        {tooltip.visible && tooltip.item && (
          <div
            className="absolute z-50 bg-slate-900/95 text-white text-[11px] rounded-md shadow-xl p-2.5 pointer-events-none border border-slate-700"
            style={{ left: tooltip.x, top: tooltip.y, minWidth: '270px' }}
          >
            <div className="font-bold mb-1.5">Recurso: {tooltip.item.name}</div>
            <div className="text-slate-200">Carga total: {formatMinutesShort(tooltip.total)}</div>
            <div className="text-slate-200">Trabalho (TRA): {formatMinutesShort(tooltip.item.trabalho)}</div>
            <div className="text-slate-200">Espera (NVA): {formatMinutesShort(tooltip.item.espera)}</div>
            <div className="text-slate-200">Takt: {formatMinutesShort(safeTakt)}</div>
            <div className="text-slate-200">Ocupacao: {tooltip.item.ocupacao.toFixed(1)}%</div>
            {tooltip.excessPercent > 0 && (
              <div className="text-amber-300 font-semibold mt-1">
                Excede takt em +{tooltip.excessPercent.toFixed(0)}%
              </div>
            )}
            <div className="text-slate-300 text-[10px] mt-1.5 border-t border-slate-600 pt-1.5">
              Eficiencia da etapa (TRA/carga): {tooltip.efficiency.toFixed(1)}%
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-[11px] px-2">
        <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
          <span className="flex items-center gap-1">
            <User size={11} className="text-slate-400" />
            <b>{safeData.length}</b> recursos
          </span>
          <span className="flex items-center gap-1">
            <span className="text-slate-400">Escala Y:</span>
            <b>0 – {Math.round(limiteSuperior)} min</b>
          </span>
        </div>

        <div
          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-semibold ${
            overloadedCount > 0
              ? 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400'
              : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400'
          }`}
        >
          {overloadedCount > 0 ? (
            <>
              <AlertTriangle size={11} />
              {overloadedCount} acima do Takt
            </>
          ) : (
            <>
              <span>OK</span>
              Fluxo balanceado
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default YamazumiChart;
